import {AxiosError} from 'axios';
import React, {useState} from 'react';
import {useTranslation} from 'react-i18next';
import {MutationStatus} from 'react-query';
import {CredentialType} from 'web-library/models/AccountManagementConfiguration';
import {GenericErrorResponse} from '../../../api';
import {
  CreateCandidateRequest,
  usePostCreateCandidate,
} from '../../../api-refactored/user/usePostCreateCandidate';
import {UseCustomMutationReturn} from '../../../api-refactored/utils/useCustomMutation';
import {CandidateProfileFormData, CandidateProfileIsDisableInputs} from '../../../models';
import {getNullIfEmptyString} from '../../../utils/StringUtils';
import {dayjsToPayloadFormat} from 'web-library';
import {convertDisplayNameToPrStatus} from '../../accountManagement/logic/usePrCitizenshipForm';
import {
  usePostVerifyCandidateByOpsAdmin,
  VerifyCandidateRequest,
} from '../../../api-refactored/user/usePostVerifyCandidate';

interface CreateCandidateFormDefaultValue extends Partial<CandidateProfileFormData> {
}

export interface CreateCandidateFormLocationStateFormData {
  formDataDefaultValues: CreateCandidateFormDefaultValue;
  formDataIsDisabled: CandidateProfileIsDisableInputs;
}

export interface CreateCandidateFormLocationState {
  formData?: CreateCandidateFormLocationStateFormData;
  credentialType?: CredentialType;
  hasMyInfoProfile?: boolean;
  redirectFromMyInfo?: boolean;
}

export interface useCreateCandidateFormReturn {
  /**
   * Function to trigger api to create candidate
   */
  createCandidate: (formData: CandidateProfileFormData, credentialType?: CredentialType) => void;
  /**
   * Extra handling error message for the create candidate api
   */
  submitError: string;
  /**
   * React query mutation object
   */
  createCandidateMutation: UseCustomMutationReturn<
      any,
      AxiosError<GenericErrorResponse>,
      CreateCandidateRequest,
      unknown
  >;
}

/**
 * A hook to handle candidate creation logic
 *
 * @param onSuccessCallback callback after create candidate API returns success response
 * @param setApiButtonStatus
 */
export const useCreateCandidateForm = (
    onSuccessCallback: () => void,
    setApiButtonStatus: React.Dispatch<React.SetStateAction<MutationStatus>>
): useCreateCandidateFormReturn => {
  const [submitError, setSubmitError] = useState('');
  const {t} = useTranslation();

  const createCandidateMutation = usePostCreateCandidate({
    onSuccess: () => {
      setApiButtonStatus('loading');
      onSuccessCallback();
    },
    onError: (err) => {
      setApiButtonStatus('error');
      createCandidateFailCallback(err);
    },
  });

  const createCandidateFailCallback = (err: AxiosError<GenericErrorResponse>) => {
    const applicationCode: string = err.response?.data.code!;
    switch (err.response?.status) {
      case undefined:
        setSubmitError('');
        break;
      case 400:
        if (applicationCode === '1002') {
          setSubmitError('Missing required information.');
        } else if (applicationCode === '1202') {
          setSubmitError('Candidate account with the same NRIC/FIN/Passport No. has been used');
        } else if (applicationCode === '1214') {
          setSubmitError(t('profile->>CreateCandidateForm.InvalidPrStatus'));
        } else {
          setSubmitError('Bad Request');
        }
        break;
      case 403:
        if (applicationCode === '1202') {
          setSubmitError('Candidate account with the same NRIC/FIN/Passport No. has been used');
        } else if (
            applicationCode === '1007' &&
            err.response?.data.message === 'You already have an existing registration.'
        ) {
          setSubmitError('Candidate already have an existing registration.');
        } else if (
            applicationCode === '1007' &&
            err.response?.data.message ===
            'You do not have access to create account. Please contact SEAB for more details.'
        ) {
          setSubmitError('Candidate is blacklisted.');
        } else {
          setSubmitError(err.response?.data.message!);
        }
        break;
      default:
        setSubmitError('Unknown error. Please try again.');
        break;
    }
  };
  const verifyCandidateMutation = usePostVerifyCandidateByOpsAdmin({
    onSuccess: () => {
      setSubmitError('');
      setApiButtonStatus('loading');
      const data: CreateCandidateRequest = {
        user: {
          name: getNullIfEmptyString(formDataLocal.name),
          email: getNullIfEmptyString(formDataLocal.email),
          countryCode: getNullIfEmptyString(formDataLocal.mobileCountryCode?.codeValue?.toString()),
          contactNumber: getNullIfEmptyString(formDataLocal.mobileContactNumber),
          createdByUserId: 0,
          updatedByUserId: 0,
          enabled: false,
          dateOfBirth: getNullIfEmptyString(formDataLocal.dateOfBirth),
        },
        userIdType: formDataLocal.IdType === 'I' ? 'nric/fin' : 'passport',
        candidateId: getNullIfEmptyString(formDataLocal.NricFin),
        hanyuPinyinName: '',
        gender: getNullIfEmptyString(formDataLocal?.gender?.codeValueDescription),
        race: getNullIfEmptyString(formDataLocal?.race?.codeValueDescription),
        secondRace: getNullIfEmptyString(formDataLocal.secondaryRace),
        nationality: getNullIfEmptyString(formDataLocal?.nationality?.codeValueDescription),
        prStatus: convertDisplayNameToPrStatus(
            getNullIfEmptyString(formDataLocal?.prStatus?.codeValueDescription)
        ),
        nationalityIssueDate: getNullIfEmptyString(formDataLocal.nationalityIssueDate),
        citizenship: getNullIfEmptyString(formDataLocal?.citizenship?.codeValueDescription),
        occupation: formDataLocal.occupation,
        organisationId: formDataLocal?.school?.id ?? null,

        // addresses
        localAddressBlockNumber: getNullIfEmptyString(formDataLocal.houseOrBlkNo),
        localAddressStreetName: getNullIfEmptyString(formDataLocal.streetName),
        localAddressBuildingName: getNullIfEmptyString(formDataLocal.building),
        localAddressFloorNumber: getNullIfEmptyString(formDataLocal.floor),
        localAddressUnitNumber: getNullIfEmptyString(formDataLocal.unit),
        localAddressPostalCode: getNullIfEmptyString(formDataLocal.postalCode),
        foreignAddress1: getNullIfEmptyString(formDataLocal.foreignAddress1),
        foreignAddress2: getNullIfEmptyString(formDataLocal.foreignAddress2),
        foreignAddress3: getNullIfEmptyString(formDataLocal.foreignAddress3),
        isLocalAddressPreferred: formDataLocal.preferredAddress === 'L',

        // contact numbers
        telephoneNumberCountryCode:
            parseInt(formDataLocal.telephoneCountryCode?.codeValue || '', 10) || null,
        telephoneNumber: getNullIfEmptyString(formDataLocal.telephoneContactNumber),
        mobileNumberCountryCode:
            parseInt(formDataLocal.mobileCountryCode?.codeValue || '', 10) || null,
        mobileNumber: getNullIfEmptyString(formDataLocal.mobileContactNumber),
        isMobileNumberPreferred: formDataLocal.preferredTelephoneOrMobile === 'M',

        // next of kin
        nextOfKinName: getNullIfEmptyString(formDataLocal.nextOfKinName),
        nextOfKinRelationship: getNullIfEmptyString(
            formDataLocal?.nextOfKinRelationshipToCandidate?.codeValueDescription
        ),
        nextOfKinContactNumberCountryCode:
            parseInt(formDataLocal.nextOfKinCountryCode?.codeValue || '', 10) || null,
        nextOfKinContactNumber: getNullIfEmptyString(formDataLocal.nextOfKinContactNumber),

        iexamsId: '',
        credentialType: credentialTypeLocal,

        currentAge: null,
      };
      createCandidateMutation.mutate(data);
    },
    onError: (err) => {
      setApiButtonStatus('error');
      createCandidateFailCallback(err);
    },
  });

  const [formDataLocal, setFormDataLocal] = useState<CandidateProfileFormData>({});
  const [credentialTypeLocal, setCredentialTypeLocal] = useState<CredentialType>('USERNAME');

  const createCandidate = (
      formData: CandidateProfileFormData,
      credentialType: CredentialType = 'USERNAME'
  ) => {
    setFormDataLocal(formData);
    setCredentialTypeLocal(credentialType);
    setApiButtonStatus('loading');
    const dataForVerification: VerifyCandidateRequest = {
      candidateName: getNullIfEmptyString(formData.name),
      userIdentityNumber: getNullIfEmptyString(formData.NricFin),
      userIdType: formData.IdType === 'I' ? 'NRIC_OR_FIN' : 'PASSPORT',
      organisationName: null,
      organisationUuid: formData?.school?.uuid.toString(),
      dateOfBirth: formData.dateOfBirth,
    };
    verifyCandidateMutation.mutate(dataForVerification);
  };

  return {
    createCandidate,
    submitError,
    createCandidateMutation,
  };
};
